import Loader from '@/components/Loaders/Loader';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import { toast } from '@/components/toast';
import uploadImage from '@/images/uploadIcon.svg';
import {
    GetDurationFromVideoProps,
    getDurationFromVideoWithFallback,
    validateVideo,
} from '@/pages/translation/components/CreateTranslationModal/utils';
import { useFlag } from '@/services/featureFlags';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { Typography } from '@mui/material';
import { ReactElement, useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { TranslatingVideosTips } from './TranslatingVideosTips';

const VideoUploadBox = styled('div', { shouldForwardProp: prop => prop !== 'isDragActive' && prop !== 'loading' })<{
    isDragActive: boolean;
    loading: boolean;
}>`
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    color: #242625;
    height: 100%;
    border: 1px ${({ theme, isDragActive }) => (isDragActive ? `solid ${theme.colors.primary[900]}` : `dashed ${theme.colors.black.main}`)};
    border-radius: 8px;
    position: relative;
    gap: 7px;
    outline: none;
    background: ${({ theme, isDragActive }) => (isDragActive ? `${theme.colors.primary.bgLight}` : '#fff')};

    &:hover {
        cursor: pointer;
        background-color: ${({ theme, isDragActive }) => !isDragActive && theme.colors.black[50]};
    }
    opacity: ${({ loading }) => (loading ? 0.3 : 1)};
    pointer-events: ${({ loading }) => (loading ? 'none' : 'auto')};
`;
const DropTextContainer = styled.div`
    display: flex;
    flex-direction: column;
`;
const DropText = styled.span`
    color: ${({ theme }) => theme.colors.secondary.main};
    font-size: 20px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    letter-spacing: -0.4px;
`;
const StyledTypography = styled(Typography)`
    color: ${({ theme }) => theme.colors.black[700]};
    font-weight: 500;
    line-height: normal;
    letter-spacing: -0.32px;
    text-align: center;
`;
const LoaderContainer = styled.div`
    height: 100%;
    padding: 5vh 5vh;
    display: flex;
    align-items: center;
    flex-direction: column;
    gap: 15px;
`;

interface Props {
    handleUploadFile: (file: File) => Promise<string>;
    setUnsupportedDuration: (duration: number) => void;
    isBrowserSupportingFormat?: boolean;
    setIsBrowserSupportingFormat: (isSupported: boolean) => void;
}

export const VideoInput = ({
    handleUploadFile,
    setUnsupportedDuration,
    isBrowserSupportingFormat,
    setIsBrowserSupportingFormat,
}: Props): ReactElement => {
    const theme = useTheme();
    const [loadingFile, setLoadingFile] = useState(false);

    const shouldEnableLongTranslations = useFlag('enable_long_translation', false);
    const onDrop = useCallback(async (files: File[]) => {
        setLoadingFile(true);
        const file = files[0];
        let res: GetDurationFromVideoProps = { isSupportedFormat: true };
        let isValid = false;

        try {
            res = await getDurationFromVideoWithFallback(file, handleUploadFile, setIsBrowserSupportingFormat);
            isValid = validateVideo(file, res.duration, shouldEnableLongTranslations);
        } catch {
            toast.error({ title: 'Could not analyze video', toastId: `upload-translation-video-error` });
            isValid = false;
        }

        if (isValid) {
            // note : we upload unsupported files to get the duration, so we don't need to upload it again.
            if (res.isSupportedFormat) {
                handleUploadFile(file);
            } else {
                if (res.duration) {
                    setUnsupportedDuration(res.duration);
                }
            }
        }
        setLoadingFile(false);
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: { 'video/mp4': ['.mp4', '.MP4'], 'video/mpeg': ['.mpeg', '.MPEG'], 'video/mov': ['.mov', '.MOV'] },
        multiple: false,
    });

    return (
        <VideoUploadBox {...getRootProps()} isDragActive={isDragActive} loading={loadingFile}>
            {loadingFile && !isBrowserSupportingFormat ? (
                <LoaderContainer>
                    <Loader style={{ width: '30%' }} />
                    <StyledTypography variant="body2">
                        Analyzing file...
                        <br />
                        This might take a few seconds
                    </StyledTypography>
                </LoaderContainer>
            ) : (
                <>
                    <input data-testid="translations-upload-video-input" {...getInputProps()} disabled={loadingFile} />
                    {isDragActive ? (
                        <DropTextContainer>
                            <DropText>Drop to upload</DropText>
                        </DropTextContainer>
                    ) : (
                        <>
                            <MaskedIcon src={uploadImage} color={theme.colors.secondary.main} size="44px" />
                            <Typography variant="h3" fontWeight={500}>
                                Click to upload a file or drag it here
                            </Typography>
                            <Typography variant="body2" color={theme.colors.black[700]} fontWeight={500}>
                                Up to 2GB. MP4, MOV, MPEG
                            </Typography>
                            <TranslatingVideosTips />
                        </>
                    )}
                </>
            )}
        </VideoUploadBox>
    );
};
